import type { Store } from 'effector'
import type { RouterResponse } from '~/core/router'
import type { Context, RouteData, RouteHistory } from './index.h'

import { createEvent, createStore, restore, sample } from 'effector'
import { previous } from 'patronum'
import { hooks } from '~/core/router'
import { entitiesPlayerTrailerModel } from '~/entities/player/trailer'
import { STATIC_IDENTIFIERS } from '~/shared/appearance'
import { aye } from '~/shared/helpers'
import { KEY_MAP } from '~/shared/routes/query'
import { DEFAULT } from './index.h'
import {
  checkPropertyByDepth,
  getPartsFromHistory,
  getPropertyByDepth,
  IDENTIFIER_TO_ANALYTIX,
  MODULE_NAMES_BY_IDENTIFIER,
  mergePartSToString,
} from './lib'

/**
 * Initial route-history resolver used to produce `contentOpenFrom`
 * for `player_open` analytics.
 */

const updateRoute = createEvent<RouterResponse>()
const foundModule = createEvent<{ name: string; route: RouterResponse }>()
const setNewModule = createEvent<string>()
const setNewCustomModule = createEvent<{ from: string; to: string }>()
const routeInited = createEvent<RouteData>()

export const setHistory = createEvent<{
  history: RouteHistory
  route?: RouterResponse
}>()
export const navbarRedirect = createEvent()

/** Marks the next player open as coming from in-player recommendations. */
export const markOpenedFromRecommendation = createEvent()
const clearOpenedFromRecommendation = createEvent()

hooks.register(hooks.Init, routeInited)

const $latestModule = createStore('').on(
  setNewModule,
  (_, newModule) => IDENTIFIER_TO_ANALYTIX[newModule] ?? newModule
)

const $customModule = createStore<{ from: string; to: string } | null>(null)
  .on(setNewCustomModule, (_, newModule) => newModule)
  .on(setNewModule, (state, newModule) =>
    state?.from === newModule ? state : null
  ) // clear on completely new module
  .reset(navbarRedirect)

const $openedFromRecommendation = createStore(false)
  .on(markOpenedFromRecommendation, () => true)
  .reset(clearOpenedFromRecommendation, navbarRedirect, setNewModule)

// Current module history deep level
const $deep = createStore<number>(0)
  .on(setHistory, (state, _ignore) => state + 1)
  .on(setNewModule, () => 0)

/*
 * This sample keeps track of latest user visited module name.
 * 'setNewModule' called on root module pages
 * and resets deep level - so history from previous module ignored.
 */
sample({
  clock: updateRoute,
  source: $latestModule,
  filter: (_, { __resolved }) => {
    const resolved = __resolved as Context
    if (checkPropertyByDepth(resolved, '/modules', 3)) return true
    if (checkPropertyByDepth(resolved, '/:identifier', 1)) return true
    return false
  },
  fn: (latestModule, route) => {
    const { __resolved, props } = route
    const resolved = __resolved as Context
    const path = getPropertyByDepth(
      resolved,
      1,
      MODULE_NAMES_BY_IDENTIFIER[STATIC_IDENTIFIERS.HOME]
    )

    if (checkPropertyByDepth(resolved, '/:identifier', 1))
      return { name: props.identifier, route }
    if (checkPropertyByDepth(resolved, '/modules', 3))
      return { name: path.slice(1), route }

    return { name: latestModule, route }
  },
  target: foundModule,
})

sample({
  clock: foundModule,
  fn: ({ name }) => name,
  target: setNewModule,
})

/*
 * Save latest custom module
 */
sample({
  clock: foundModule,
  filter: ({ route }) => {
    const query = route?.props?.query
    return aye(query) && (query.has('module') || query.has(KEY_MAP.module))
  },
  fn: ({ name, route }) => {
    const query = route.props!.query as URLSearchParams
    return {
      from: name,
      to: query.get('module') || query.get(KEY_MAP.module) || '',
    }
  },
  target: setNewCustomModule,
})

/*
 * This sample validates and forwards route and its init values to analytix feature
 */
sample({
  clock: routeInited,
  filter: (data) => aye(data) && aye(data.route) && aye(data.route.__resolved),
  fn: ({ route }) => route,
  target: updateRoute,
})

/*
 * String consists of tree parts.
 * "{ Module Name }/{ Route Name }/{ Category Name }" - routeName and categoryName may be empty
 */
export const $contentOpenFrom = createStore('')

const $history = createStore<RouteHistory>(DEFAULT).on(
  setHistory,
  (_, data) => data.history
)

/*
 * Merge module name with route data and init values
 */
sample({
  clock: updateRoute,
  source: $latestModule,
  filter: (_ignore, route) => aye(route?.props?.query),
  fn: (module, route) => {
    const meta = route?.meta

    const query = route.props.query as URLSearchParams

    const routeName =
      query.get('routeName') || query.get(KEY_MAP.routeName) || meta?.title
    const categoryName =
      query.get('categoryName') || query.get(KEY_MAP.categoryName)

    return {
      history: { module, routeName, categoryName },
      route,
    }
  },
  target: setHistory,
})

const $previous: Store<RouteHistory> = previous($history, DEFAULT)

const $beforePrevious: Store<RouteHistory> = previous($previous, DEFAULT)

const playerPageEntered = createEvent()

sample({
  clock: setHistory,
  source: restore(updateRoute, null),
  filter: (fallbackRoute, { route }) => {
    const resultRoute = route ?? fallbackRoute
    const parent = (resultRoute?.__resolved as Context | undefined)?.parent
    return parent?.path === '/player' || parent?.path === '/epg' // pages with player component
  },
  target: playerPageEntered,
})

const autoTrailerStarts = createEvent()

// if player is about to be rendered on page, fire autoTrailerStarts
sample({
  clock: entitiesPlayerTrailerModel.$shouldRenderTrailerPlayer,
  filter: (shouldRender) => aye(shouldRender),
  target: autoTrailerStarts,
})

const openFromOverrides = createEvent<RouteHistory>()

/*
 * Generate opend from string, when player page opened
 */
sample({
  clock: playerPageEntered,
  source: {
    current: $history,
    previous: $previous,
    beforePrevious: $beforePrevious,
    deep: $deep,
    // overrides
    customModule: $customModule,
    fromRecommendation: $openedFromRecommendation,
  },
  fn: (source) => {
    if (source.fromRecommendation) {
      return { module: 'Recommendation' }
    }

    const parts = getPartsFromHistory(source)

    const { customModule } = source

    return {
      module: customModule ? customModule.to : parts.module,
      routeName: parts.routeName,
      categoryName: parts.categoryName,
    }
  },
  target: openFromOverrides,
})

sample({
  clock: playerPageEntered,
  source: $openedFromRecommendation,
  filter: Boolean,
  target: clearOpenedFromRecommendation,
})

/*
 * Generate opend from string, when auto trailer starts
 */
sample({
  clock: autoTrailerStarts,
  source: {
    current: $history,
    previous: $previous,
    beforePrevious: $beforePrevious,
    deep: $deep,
    // overrides
    customModule: $customModule,
    params: entitiesPlayerTrailerModel.$trailerFlowParams,
  },
  fn: (source) => {
    const parts = getPartsFromHistory(source)

    const { customModule, params } = source

    return {
      module: customModule ? customModule.to : parts.module,
      routeName: params?.routeName ?? parts.routeName,
      categoryName: params?.categoryName ?? parts.categoryName,
    }
  },
  target: openFromOverrides,
})

sample({
  clock: openFromOverrides,
  fn: mergePartSToString,
  target: $contentOpenFrom,
})
